// fragmentAtoms.js
/**
 * @fileoverview Build a fragment pose's Atom/Bond objects on demand from its shared baseFrag
 * template plus the pose's own orientation + translation.
 *
 * A Fragment Data Query over a large kinase panel can return ~10^5 poses across ~100 protein
 * cases. Every pose of a given fragment has identical chemistry (atom names, elements, charges,
 * bond topology) -- only the 3D coordinates differ -- and that invariant chemistry already lives
 * once on the shared `baseFrag`. Materializing a full Atom/Bond graph for every pose at decode
 * time therefore duplicated the invariant data ~nPoses times and was the client's decode-time
 * OOM at panel scale (~908 MB from a ~5 MB blob).
 *
 * So decode now keeps only the lightweight DecodedFragment (baseFrag ref + translation +
 * orientation + a reserved `serialBase`), and the atoms for a pose are built here lazily the
 * first time `DecodedFragment.getAtoms()` is called -- i.e. only for poses actually displayed,
 * added as a compound, or grown. `orientationToMatrix` is also used by the single-case
 * `decodeFragments` path in decode.js (which still builds eagerly because those atoms join the
 * scene / selection registry), so it lives here as the shared home.
 */
import { Atom, Bond } from './atoms';
import { amberToElementMap, knownLigandChargesMap } from '../atomnames';

// 3-vector helpers (previously defined inline in decode.js). Kept local so this module depends
// only on atoms.js + atomnames (no cycle back through decode.js / atomgroups.js).
const add3_3 = (v1, v2) => [v1[0] + v2[0], v1[1] + v2[1], v1[2] + v2[2]];
const mul3x3_3 = (m, v) => [
    m[0][0] * v[0] + m[0][1] * v[1] + m[0][2] * v[2],
    m[1][0] * v[0] + m[1][1] * v[1] + m[1][2] * v[2],
    m[2][0] * v[0] + m[2][1] * v[1] + m[2][2] * v[2],
];

// Orientation vectors are stored as floats, so use float resolution.
const FLT_EPSILON = 1.19209290e-07;
const zeroThreshold = 2 * FLT_EPSILON;

function decomposeOrientation(o, offset) {
    const vecmag = Math.sqrt(o[0] * o[0] + o[1] * o[1] + o[2] * o[2]);
    let angle;
    let u;
    if (vecmag <= zeroThreshold) {
        angle = 0;
        u = [0, 0, 0];
    } else {
        angle = vecmag - offset;
        u = [o[0] / vecmag, o[1] / vecmag, o[2] / vecmag];
    }
    return [u, angle];
}

/**
 * Rotation matrix for a fragment pose's orientation vector (from bmoc/bgeom.elp).
 * @param {number[]} ovec orientation vector
 * @param {number} offset baseFrag.orientationOffset
 * @returns {number[][]} 3x3 rotation matrix
 */
export function orientationToMatrix(ovec, offset) {
    const [unitv, rawAngle] = decomposeOrientation(ovec, offset);
    if (rawAngle === 0) { // exact compare ok here
        return [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
    }

    // Components of a unit direction vector
    const [x, y, z] = unitv;

    // normalize angle
    const angle = rawAngle % (Math.PI * 2);

    let c = Math.cos(angle);
    let s = Math.sqrt(1 - c * c); // see below for sign
    const t = 1 - c;

    // Enforce sign consistency.
    if (Math.abs(angle - Math.PI) <= zeroThreshold) {
        c = -1;
        s = 0;
    } else if (angle > Math.PI) s = -s;

    return [[t * x * x + c, t * x * y - s * z, t * x * z + s * y],
        [t * x * y + s * z, t * y * y + c, t * y * z - s * x],
        [t * x * z - s * y, t * y * z + s * x, t * z * z + c]];
}

/**
 * Materialize the Atom objects for a single decoded fragment pose from its baseFrag template,
 * applying the pose's rotation (orientation) + translation. Also creates the pose's Bond objects,
 * which attach themselves to their two atoms (so `atom.bonds` is populated). Returns the atoms.
 *
 * Requires `frag.serialBase` (reserved by decode, so atom serials match the previous eager
 * numbering), `frag.baseFrag`, `frag.orientation`, `frag.translation`.
 * @param {import('./atomgroups').DecodedFragment} frag
 * @returns {Atom[]}
 */
export function buildFragmentAtoms(frag) {
    const {
        baseFrag, translation: t, orientation: o, serialBase,
    } = frag;
    const m = orientationToMatrix(o, baseFrag.orientationOffset);
    const bfatoms = baseFrag.atoms;
    const isWater = baseFrag.name === 'water';

    const fatoms = bfatoms.map((bfatom, i) => {
        const pos = add3_3(mul3x3_3(m, bfatom.pos), t);
        const serial = serialBase + i;
        const fatom = new Atom(
            frag, serial, bfatom.name, pos, serial, bfatom.amber,
            amberToElementMap.get(bfatom.amber),
        );
        fatom.charge = isWater && bfatom.name === 'O1'
            ? knownLigandChargesMap.get('HOH')[0]
            : bfatom.charge;
        return fatom;
    });

    // Second pass for bonds, once all atoms exist. Bond's constructor wires itself onto both atoms.
    for (const bfatom of bfatoms) {
        for (const bfbond of bfatom.bonds) {
            // eslint-disable-next-line no-new
            new Bond(fatoms[bfatom.index], fatoms[bfbond.index], bfbond.type, bfbond.kekuletype);
        }
    }
    return fatoms;
}
